import { User, UserGroups } from '@ravago/shared/authentication';
import { getLogger } from '@ravago/shared/logging/client';
import RevalidationTag from '@ravago/shared/page-data/enums/revalidation-tags/tags';
import { ContentfulCollectionName } from '@ravago/shared/page-data/models/contentful';
import { TypeCaseStudyFields } from '@ravago/shared/page-data/models/contentful-generated';
import { NormalizedCaseStudy, NormalizedCollection } from '@ravago/shared/page-data/models/contentful-mapped';
import { BaseQuery } from '@ravago/shared/page-data/models/utils';
import { ContentfulUtils } from '@ravago/shared/page-data/utils/contentful';
import { DataMapperUtils } from '@ravago/shared/page-data/utils/data-mapper';
import { validateAndFilterCaseStudyEntries } from '@ravago/shared/page-data/validators';

import { ContentfulClientApi } from 'contentful';

import { cache } from '../cache/cache.utils';
import { ContentfulProductUtils } from '../contentful-product/contentful-product.utils';
import { createClient } from '../contentful/client';
import { MarketSegmentUtils } from '../market-segment/market-segment.utils';
import { QueryBuilder } from '../query-builder/query-builder';

/* c8 ignore start */
export class CaseStudyUtils {
  static getCaseStudies = cache(async (locale: string, query: BaseQuery, user: User, overwriteClientApi?: ContentfulClientApi): Promise<NormalizedCollection<NormalizedCaseStudy>> => {
    const contentfulClientApi = overwriteClientApi ?? createClient([RevalidationTag.caseStudies]);
    const contentType: ContentfulCollectionName = 'caseStudy';

    const { limit, orderBy, caseStudyIds, productIds, industryIds, filterBy, brandIds } = query;

    const [productId] = productIds || [];
    const [brandId] = brandIds || [];

    const userInternal = user.groups.includes(UserGroups.ravagoSalesRepresentative) || user.groups.includes(UserGroups.ravagoGlobalGroupAdmin);

    const queryOptions = new QueryBuilder()
      .contentType(contentType)
      .limit(limit || 4)
      .order(orderBy)
      .include(10)
      .locale(locale)
      .setArrayOptional('sys.id[in]', caseStudyIds)
      .setOptional('fields.products.sys.id', productId);

    if (!userInternal) {
      queryOptions.set('fields.visibility[all]', 'External');
    }

    if (brandId) {
      const products = await ContentfulProductUtils.getProductCollection(
        locale,
        {
          limit: 1000,
          filterBy,
          orderBy,
          brandIds: [brandId],
          select: 'sys.id, fields.slug'
        },
        user,
        overwriteClientApi
      );

      queryOptions.setArrayOptional(
        'fields.products.sys.id[in]',
        products.items.map((prod) => prod.id)
      );
    }

    if (industryIds?.length) {
      const marketSegments = await MarketSegmentUtils.getMarketSegments(
        locale,
        {
          limit: 1000,
          filterBy,
          orderBy,
          industryIds
        },
        overwriteClientApi
      );
      const marketSegmentIds: Array<string> = marketSegments.items.map((marketSegment) => marketSegment.id) || [];

      queryOptions.setArrayOptional('fields.segments.sys.id[in]', marketSegmentIds);
    }

    try {
      const entries = await contentfulClientApi.getEntries<TypeCaseStudyFields>(queryOptions.build());

      if (!entries.total) return ContentfulUtils.getDefaultCollection();

      const validEntries = validateAndFilterCaseStudyEntries(entries.items);

      entries.items = validEntries;

      const response = DataMapperUtils.normalizeContentfulCollection<NormalizedCollection<NormalizedCaseStudy>>(entries, DataMapperUtils.normalizeContentfulCaseStudy);

      response.items = response.items.slice(0, limit || 4);

      return response;
    } catch (error) {
      getLogger().error(error instanceof Error ? error.message : 'Unknown error occurred.');

      return ContentfulUtils.getDefaultCollection();
    }
  });

  static getAll = cache(async (contentfulClientApi: ContentfulClientApi, locale: string): Promise<NormalizedCollection<NormalizedCaseStudy>> => {
    const contentType: ContentfulCollectionName = 'caseStudy';

    const queryOptions = new QueryBuilder().contentType(contentType).limit(1000).include(10).locale(locale);

    try {
      const entries = await contentfulClientApi.getEntries<TypeCaseStudyFields>(queryOptions.build());

      while (entries.total > entries.items.length) {
        const nextEntries = await contentfulClientApi.getEntries<TypeCaseStudyFields>(queryOptions.set('skip', entries.items.length).build());

        entries.items.push(...nextEntries.items);
      }

      if (!entries.total) return ContentfulUtils.getDefaultCollection();

      const validEntries = validateAndFilterCaseStudyEntries(entries.items);

      entries.items = validEntries;

      return DataMapperUtils.normalizeContentfulCollection<NormalizedCollection<NormalizedCaseStudy>>(entries, DataMapperUtils.normalizeContentfulCaseStudy);
    } catch (error) {
      getLogger().error(error instanceof Error ? error.message : 'Unknown error occurred.');

      return ContentfulUtils.getDefaultCollection();
    }
  });
}

/* c8 ignore end */
